/**
 * API configuration and shared fetch client.
 *
 * In development, the proxy in package.json forwards requests to the PHP backend.
 * In production, the frontend is served from the same origin as the API.
 * Either way, relative paths work — no hardcoded domain needed.
 */

/**
 * Shared fetch wrapper. Handles credentials, JSON headers, and error parsing.
 *
 * Usage:
 *   const data = await api.get(API.PROJECTS);
 *   const data = await api.post(API.PROJECTS_CREATE, { title: '...' });
 *   const data = await api.upload(API.EXPLOITS_UPLOAD, formData);
 */
export const api = {
    async get(url) {
        const res = await fetch(url, { method: 'GET', credentials: 'include' });
        return res.json();
    },

    async post(url, body) {
        const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(body),
        });
        return res.json();
    },

    async put(url, body) {
        const res = await fetch(url, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(body),
        });
        return res.json();
    },

    async del(url) {
        const res = await fetch(url, { method: 'DELETE', credentials: 'include' });
        return res.json();
    },

    async upload(url, formData) {
        const res = await fetch(url, {
            method: 'POST',
            credentials: 'include',
            body: formData,
        });
        return res.json();
    },
};

const API = {
    // Auth
    LOGIN:              '/api/auth/login',
    LOGOUT:             '/api/auth/logout',
    CHECK_AUTH:         '/api/auth/check',
    CHECK_ADMIN:        '/api/auth/check-admin',
    DASHBOARD:          '/api/auth/dashboard',

    // Projects
    PROJECTS:           '/api/projects',
    PROJECTS_CREATE:    '/api/projects',
    PROJECTS_UPDATE:    '/api/projects/update',
    PROJECTS_DELETE:    '/api/projects/delete',

    // Exploits
    EXPLOITS:           '/api/exploits',
    EXPLOITS_UPLOAD:    '/api/exploits/upload',
    EXPLOITS_UPDATE:    '/api/exploits/update',
    EXPLOITS_DELETE:    '/api/exploits/delete',
    EXPLOITS_DOWNLOAD:  '/api/exploits/download',
    EXPLOITS_CHECK_FILE:'/api/exploits/check-file',

    // Users
    USERS:              '/api/users',
    USERS_CREATE:       '/api/users/create',
    USERS_DELETE:       '/api/users/delete',
    USER_PROFILE:       '/api/users/profile',
    USER_EDIT_PROFILE:  '/api/users/profile',
    ROSTER:             '/api/users/roster',
    RESET_PASSWORD:     '/api/users/reset-password',

    // Admin
    FAILED_LOGINS:      '/api/admin/failed-logins',
    SECURITY_METRICS:   '/api/admin/security-metrics',
    NOTIFICATIONS:      '/api/admin/notifications',
    SYSTEM_MESSAGE:     '/api/admin/notifications',
    ACTIVITY_LOGS:      '/api/admin/activity-logs',

    // Training
    TRAINING:           '/api/training',
    TRAINING_CREATE:    '/api/training/create',
    TRAINING_SUBMIT:    '/api/training/submit-flag',
    TRAINING_DELETE:    (id) => `/api/training/${id}`,

    // Writeups
    WRITEUPS:           '/api/writeups',
    WRITEUPS_PUBLIC:    '/api/writeups/public',
    WRITEUPS_DELETE:    (id) => `/api/writeups/${id}`,

    // Public
    CONTACT:            '/api/contact',
    CTF_STATS:          '/api/ctfstats',
};

export default API;
